import { Box, List, ListItemButton, ListItemIcon, ListItemText, Radio } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useForm, useController, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useClearRefinements } from 'react-instantsearch';
import { useAtomValue } from 'jotai';
import useNormalizedCurrentRefinements from '../../hooks/useNormalizedCurrentRefinements';
import useRefine from '../../hooks/useRefine';
import { DiscoveryWordingsKeys } from '../discovery.utils';
import { HistoryIndexUiState } from '../state/state';
import { currentContextAtom } from '../../state';
import FilterDisplay, { FilterDisplayMode } from './FilterDisplay';
import GeoLocationAdministrativeAreaFilter from './GeoLocationAdministrativeAreaFilter';
import GeoLocationRadiusRange from './GeoLocationRadiusRange';

enum GeoLocationFilterValueEnum {
  ALL = 'ALL',
  RANGE = 'RANGE',
  ADMINISTRATIVE_AREA = 'ADMINISTRATIVE_AREA',
}

export interface GeoLocationFormValues {
  type: GeoLocationFilterValueEnum,
  range: number;
  administrativeAreas: string[];
}

interface GeoLocationFilterProps {
  index: string;
  mode?: FilterDisplayMode;
  unmountOnExit?: boolean;
}

export default function GeoLocationFilter({ index, mode, unmountOnExit = true }: GeoLocationFilterProps): React.JSX.Element {
  const { t } = useTranslation(['discovery']);
  const administrativeAreas = useNormalizedCurrentRefinements('administrative_areas');
  const { refine: clearAdministrativeAreasRefine } = useClearRefinements({ includedAttributes: ['administrative_areas'] });
  const form = useForm<GeoLocationFormValues>({
    defaultValues: {
      type: GeoLocationFilterValueEnum.ALL,
      range: 100,
    },
  });
  const { control, watch, setValue } = form;
  const { field: typeField } = useController({ name: 'type', control });
  const [range, type] = watch(['range', 'type']);
  const { refine, uiState } = useRefine();
  const { realm } = useAtomValue(currentContextAtom);

  const activeItemCount = useMemo(() => {
    switch (type) {
      case GeoLocationFilterValueEnum.RANGE:
        return 1;
      case GeoLocationFilterValueEnum.ADMINISTRATIVE_AREA:
        return administrativeAreas.length;
      default:
        return 0;
    }
  }, [type, administrativeAreas]);

  const handleTypeChange = (value: GeoLocationFilterValueEnum): void => {
    typeField.onChange(value);

    const aroundRadius = value === GeoLocationFilterValueEnum.RANGE ? range * 1000 : 'all';

    refine({
      aroundRadius,
    });
    clearAdministrativeAreasRefine();
  };

  useEffect(() => {
    if (type === GeoLocationFilterValueEnum.RANGE && uiState.aroundRadius === 'all') {
      setValue('type', GeoLocationFilterValueEnum.ALL);
    }
  }, [uiState]);

  useEffect(() => {
    if (administrativeAreas.length) {
      setValue('type', GeoLocationFilterValueEnum.ADMINISTRATIVE_AREA);

      refine({
        aroundRadius: 'all',
      });
    }
  }, [administrativeAreas.length]);

  useEffect(() => {
    const historyIndexUiState = window.history.state.uiState?.[index] as HistoryIndexUiState | undefined;

    if (historyIndexUiState?.configure && historyIndexUiState.configure.aroundRadius !== 'all') {
      setValue('type', GeoLocationFilterValueEnum.RANGE);

      if (historyIndexUiState.configure.aroundRadius) {
        setValue('range', historyIndexUiState.configure.aroundRadius / 1000);
      }
    } else if (historyIndexUiState?.refinementList?.administrative_areas) {
      setValue('type', GeoLocationFilterValueEnum.ADMINISTRATIVE_AREA);
    }
  }, []);

  return (
    <FormProvider {...form}>
      <FilterDisplay
        activeItemCount={activeItemCount}
        label={t('discovery:distance')}
        mode={mode}
        name="geolocation"
        unmountOnExit={unmountOnExit}
        content={(
          <List>
            <ListItemButton
              onClick={() => handleTypeChange(GeoLocationFilterValueEnum.ALL)}
            >
              <ListItemText primary={t('discovery:search_without_limit')} />
              <ListItemIcon>
                <Radio
                  edge="start"
                  checked={type === GeoLocationFilterValueEnum.ALL}
                />
              </ListItemIcon>
            </ListItemButton>
            <ListItemButton
              onClick={() => handleTypeChange(GeoLocationFilterValueEnum.RANGE)}
            >
              <ListItemText primary={t(DiscoveryWordingsKeys[realm].geoloc_radius_label)} />
              <ListItemIcon>
                <Radio
                  edge="start"
                  checked={type === GeoLocationFilterValueEnum.RANGE}
                />
              </ListItemIcon>
            </ListItemButton>
            {type === GeoLocationFilterValueEnum.RANGE && (
              <Box sx={{ px: 2, pb: 1 }}>
                <GeoLocationRadiusRange />
              </Box>
            )}
            <ListItemButton
              onClick={() => handleTypeChange(GeoLocationFilterValueEnum.ADMINISTRATIVE_AREA)}
            >
              <ListItemText primary={t('discovery:search_by_administrative_area')} />
              <ListItemIcon>
                <Radio
                  edge="start"
                  checked={type === GeoLocationFilterValueEnum.ADMINISTRATIVE_AREA}
                />
              </ListItemIcon>
            </ListItemButton>
            <Box
              sx={{
                display: type === GeoLocationFilterValueEnum.ADMINISTRATIVE_AREA ? 'flex' : 'none',
                flexDirection: 'column',
                maxWidth: '100%',
              }}
            >
              <GeoLocationAdministrativeAreaFilter />
            </Box>
          </List>
        )}
      />
    </FormProvider>
  );
}
